import React, { useState, useMemo } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { Typography, Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { DateTime } from "luxon";
import { getTeamLogo, getTeamName } from "../../../utils/SportsData";
import { formatGameCardDate } from "../../../utils/DateFormatting/GameCardDateFormat";
import './SuperPickemOnePickCard.scss';
import LockIcon from '@mui/icons-material/Lock';
import { set } from "lodash";

const SuperPickemOnePickCard = ({
    game,
    timeNowInNewYork,
    thisIntervalsPick,
    setSubmitPickModalOpen,
    setSubmittedPickInfo,
}) => {
    //console.log("This Intervals Pick:", thisIntervalsPick);

    const {
        interval,
        league,
        pickState,
    } = useUserAndGameContext();

    const {
        teams
    } = useSuperPickemContext();
    //console.log("Game", game, "This Intervals pick", thisIntervalsPick);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const gameDateTime = game?.DateTime;
    const gameTimeInNewYork = DateTime.fromISO(gameDateTime, { zone: 'America/New_York' });
    const hasGameStarted = gameTimeInNewYork < timeNowInNewYork;

    const globalGameId = game?.GlobalGameID;
    const isGameSelected = pickState?.some((pick) => {
        return pick?.gameId === globalGameId;
    });
    //console.log("Is Game Selected:", isGameSelected);

    const somePickForThisInterval = pickState?.some((pick) => {
        return pick?.interval === interval;
    });
    //console.log("Some Pick For This Interval:", somePickForThisInterval);
    const homeScore = game?.HomeTeamScore ?? game?.HomeScore;
    const awayScore = game?.AwayTeamScore ?? game?.AwayScore;
    const isGameClosed = game?.IsClosed;
    const topHeaderColor = '#e5e5e5';
    const awayTeamRow = 'Away Team';
    const homeTeamRow = 'Home Team';
    const homeWins = game?.SeriesInfo?.HomeTeamWins;
    const awayWins = game?.SeriesInfo?.AwayTeamWins;
    const seriesText = homeWins === awayWins ?
        `(${homeWins} - ${awayWins})` :
        homeWins > awayWins ?
            `(${game?.HomeTeam} ${homeWins === 4 ? 'wins' : 'leads'} ${homeWins} - ${awayWins})` :
            `(${game?.AwayTeam} ${awayWins === 4 ? 'wins' : 'leads'} ${awayWins} - ${homeWins})`;
    const bottomLeftText = game?.SeriesInfo ? `Game ${game?.SeriesInfo?.GameNumber} ${seriesText}` : '';

    const isTopSelected = pickState?.some((pick) => {
        return pick?.gameId === globalGameId && pick?.teamId === game?.GlobalAwayTeamID
        });
    const isBottomSelected = pickState?.some((pick) => {
        return pick?.gameId === globalGameId && pick?.teamId === game?.GlobalHomeTeamID
        });

    const homeSpread = game?.homeSpread;
    const homePointSpread = game?.PointSpread;
    //console.log("Home Spread:", homeSpread, "Home Point Spread:", homePointSpread);

    const gameDay = game?.Day;
    const gameTime = game?.DateTime;
    const gameTimeDisplay = gameTime ? formatGameCardDate(gameTime) : formatGameCardDate(gameDay);

    const bottomSpreadDisplay = useMemo(() => {
        if (!hasGameStarted) {
            if (isTopSelected) {
                if (homeSpread === 0) {
                    return 'Pk';
                } else if (homeSpread > 0) {
                    return `${game.AwayTeam} -${homeSpread}`;
                } else {
                    return `${game.AwayTeam} +${Math.abs(homeSpread)}`;
                }
            } else {
                if (homeSpread === 0) {
                    return 'Pk';
                } else if (homeSpread > 0) {
                    return `${game.HomeTeam} +${homeSpread}`;
                } else {
                    return `${game.HomeTeam} -${Math.abs(homeSpread)}`;
                }
            }
        } else {
            if (isTopSelected) {
                if (homePointSpread === 0) {
                    return 'Pk';
                } else if (homePointSpread > 0) {
                    return `${game.AwayTeam} -${homePointSpread}`;
                } else {
                    return `${game.AwayTeam} +${Math.abs(homePointSpread)}`;
                }
            } else {
                if (homePointSpread === 0) {
                    return 'Pk';
                } else if (homePointSpread > 0) {
                    return `${game.HomeTeam} +${homePointSpread}`;
                } else {
                    return `${game.HomeTeam} -${Math.abs(homePointSpread)}`;
                }
            }
        }
    }, [homeSpread, homePointSpread, game?.AwayTeam, game?.HomeTeam, hasGameStarted, isTopSelected]);

    const bottomRightText = !isGameSelected 
        ? '' 
            : !hasGameStarted 
                ? `Live Line: ${bottomSpreadDisplay}`
                : `Closing Line: ${bottomSpreadDisplay}`;

    

    const topRightText = useMemo(() => {
        if (game?.Status === 'Final' || game?.Status === 'F/OT') {
            return game?.Status;
        } else {
            return game?.Channel;
        }
    }, [game?.Status, game?.Channel]);

    const whoWonWithSpread = useMemo(() => {
        if (!game?.IsClosed || (homeScore === undefined && awayScore === undefined)) {
            return null;
        } else {
            if (!isGameSelected) {
                if ((homeScore + homePointSpread) > awayScore) {
                    return 'home';
                } else if ((homeScore + homePointSpread) < awayScore) {
                    return 'away';
                } else if ((homeScore + homePointSpread) === awayScore) {
                    return 'push';
                }
            } else {
                if (isTopSelected) {
                    if ((awayScore + thisIntervalsPick?.spread) > homeScore) {
                        return 'away';
                    } else if ((awayScore + thisIntervalsPick?.spread) < homeScore) {
                        return 'home';
                    } else if ((awayScore + thisIntervalsPick?.spread) === homeScore) {
                        return 'push';
                    }
                } else if (isBottomSelected) {
                    if ((homeScore + thisIntervalsPick?.spread) > awayScore) {
                        return 'home';
                    } else if ((homeScore + thisIntervalsPick?.spread) < awayScore) {
                        return 'away';
                    } else if ((homeScore + thisIntervalsPick?.spread) === awayScore) {
                        return 'push';
                    }
                }
            }
        }
    }, [game?.IsClosed, homeScore, awayScore, homePointSpread, isGameSelected, thisIntervalsPick, isTopSelected, isBottomSelected]);

    const whoWon = useMemo(() => {
        if (!game?.IsClosed || (homeScore === undefined && awayScore === undefined)) {
            return null;
        } else {
            if (homeScore > awayScore) {
                return 'home';
            }
            if (homeScore < awayScore) {
                return 'away';
            }
        }
    }, [game?.IsClosed, homeScore, awayScore]);

    const getButtonColor = useMemo(() => {
        return (home) => {
            if (!isGameSelected) {
                if (!hasGameStarted) {
                    return '#E5E5E5';
                } else if ((home && whoWonWithSpread === 'home') || (!home && whoWonWithSpread === 'away')) {
                    return '#CCFFEF';
                } else if (whoWonWithSpread === 'push') { 
                    return '#5A5B5C';
                } else {
                    return '#fff';
                }
            } else if (home && isBottomSelected) {
                if (thisIntervalsPick?.result === 'win') {
                    return '#00AA72';
                } else if (thisIntervalsPick?.result === 'loss') {
                    return '#CC293C';
                } else if (thisIntervalsPick?.result === 'push') {
                    return '#5A5B5C';
                } else {
                    return '#002129';
                }
            } else if (!home && isTopSelected) {
                if (thisIntervalsPick?.result === 'win') {
                    return '#00AA72';
                } else if (thisIntervalsPick?.result === 'loss') {
                    return '#CC293C';
                } else if (thisIntervalsPick?.result === 'push') {
                    return '#5A5B5C';
                } else {
                    return '#002129';
                }
            } else if ((!home && isBottomSelected) || (home && isTopSelected)) {      
                    return '#fff';
            }
        }
    }, [isGameSelected, hasGameStarted, whoWonWithSpread, isTopSelected, isBottomSelected, thisIntervalsPick]);

    const topButtonText = useMemo(() => {
        if (isGameSelected && isBottomSelected) {
            return '';
        }

        if (isGameSelected && isTopSelected) {
            if (thisIntervalsPick?.spread === 0) {
                return 'Pk';
            } else if (thisIntervalsPick?.spread > 0) {
                return `+${thisIntervalsPick?.spread}`;
            } else {
                return `${(thisIntervalsPick?.spread)}`;
            }
        } 

        if (!hasGameStarted || !isGameClosed) {
            if (homeSpread !== undefined) {
                //console.log("HOME SPREAD:", homeSpread);
                if (homeSpread === 0) {
                    return 'Pk';
                } else if (homeSpread > 0) {
                    return `-${homeSpread}`;
                } else {
                    return `+${Math.abs(homeSpread)}`;
                }
            } else {
                return <LockIcon sx={{ fontSize: isMobile ? '20px' : '24px'}}/>;
            }
        }

        if (isGameClosed && (whoWonWithSpread === 'push' || whoWonWithSpread === 'away')) {
            if (homePointSpread !== undefined) {
                if (homePointSpread === 0) {
                    return 'Pk';
                } else if (homePointSpread > 0) {
                    return `-${homePointSpread}`;
                } else {
                    return `+${Math.abs(homePointSpread)}`;
                }
            }
        }
        return null;
    }, [homeSpread, hasGameStarted, isGameSelected, isBottomSelected, isGameClosed, isMobile, thisIntervalsPick, whoWonWithSpread, homePointSpread, isTopSelected]);
   

    const bottomButtonText = useMemo(() => {
        if (isGameSelected && isTopSelected) {
            return '';
        }

        if (isGameSelected && isBottomSelected) {
            if (thisIntervalsPick?.spread === 0) {
                return 'Pk';
            } else if (thisIntervalsPick?.spread > 0) {
                return `+${thisIntervalsPick?.spread}`;
            } else {
                return `${thisIntervalsPick?.spread}`;
            }
        }

        if (!hasGameStarted || !isGameClosed) {
            if (homeSpread !== undefined) {
                if (homeSpread === 0) {
                    return 'Pk';
                } else if (homeSpread > 0) {
                    return `+${homeSpread}`;
                } else {
                    return `${homeSpread}`;
                }
            } else {
                return <LockIcon sx={{ fontSize: isMobile ? '20px' : '24px'}}/>;
            }
        }

        

        if (isGameClosed && (whoWonWithSpread === 'push' || whoWonWithSpread === 'home')) {
            if (homePointSpread !== undefined) {
                if (homePointSpread === 0) {
                    return 'Pk';
                } else if (homePointSpread > 0) {
                    return `+${homePointSpread}`;
                } else {
                    return `${homePointSpread}`;
                }
            }
        }
            

        return null;
    }, [homeSpread, hasGameStarted, isTopSelected, isGameSelected, isMobile, isGameClosed, thisIntervalsPick, whoWonWithSpread, homePointSpread, isBottomSelected]);
    //console.log("Is Game Selected:", isGameSelected, "Is Top Selected:", isTopSelected, "Is Bottom Selected:", isBottomSelected, "Home Spread:", homeSpread, "Home Point Spread:", homePointSpread, "TopButtonText:", topButtonText, "BottomButtonText:", bottomButtonText, "Who Won With Spread:", whoWonWithSpread, "Who Won:", whoWon);


    const getBackgroundColor = useMemo(() => {
        if (!isGameSelected) {
            return '#e5e5e5'
        } else if (isGameSelected) {
            if (!thisIntervalsPick?.result) {
                return '#002129';
            } else if (thisIntervalsPick?.result === 'win') {
                return '#00AA72';
            } else if (thisIntervalsPick?.result === 'loss') {
                return '#CC293C';
            } else if (thisIntervalsPick?.result === 'push') {
                return '#5A5B5C';
            } else {
                return '#e5e5e5';
            }
        }
    }, [isGameSelected, thisIntervalsPick]);




    const handleButtonClick = (teamId, spread) => {
        const pick = {
            gameId: globalGameId,
            teamId: teamId,
            interval: interval,
            spread: spread === 'Pk' ? 0 : Number(spread),
        };
        //console.log("Pick:", pick);
        setSubmittedPickInfo(pick);
        setSubmitPickModalOpen(true);

    }
    //console.log("HasGameStarted:", hasGameStarted, "This Intervals Pick:", thisIntervalsPick, "Home Spread:", homeSpread);

    return (
        <Box className='super-pickem-game-card-container'>

            <Box 
                className='super-pickem-game-card-top-border'
                style={{ backgroundColor: getBackgroundColor }}
            >
                <Typography
                    className="super-pickem-game-card-top-left-text"
                    sx={{ color: isGameSelected ? '#fff' : '#002129' }}
                >
                    {gameTimeDisplay}
                </Typography>
                <Typography
                    className="super-pickem-game-card-top-right-text"
                    sx={{ color: isGameSelected ? '#fff' : '#002129' }}
                >
                    {topRightText}
                </Typography>
            </Box>

            <Box>
                <Box className='super-pickem-game-card-team-row'>
                    <Box className='super-pickem-game-card-logo'>
                        <img
                            src={getTeamLogo(league, game?.GlobalAwayTeamID, teams)}
                            alt={game?.AwayTeam}
                        />
                    </Box>
                    <Typography 
                        className="super-pickem-game-card-team-name"
                        style={{
                            marginLeft: '10px',
                            fontWeight: whoWon === 'away' ? 700 : 500,
                        }}
                    >
                        {getTeamName(league, game?.GlobalAwayTeamID, teams)}
                    </Typography>
                    {isGameClosed && awayScore !== undefined && homeScore !== undefined && (
                        <Typography 
                            className="super-pickem-game-card-score"
                            style={{
                                marginLeft: 'auto',
                                marginRight: '10px',
                                fontWeight: whoWon === 'away' ? 700 : 400,
                                fontSize: isMobile ? '16px' : '18px',
                            }}
                        >
                            {awayScore}
                        </Typography>
                    )}
                    <Button 
                        className='super-pickem-game-card-button'
                        style={{ backgroundColor: getButtonColor(false), color: isTopSelected ? '#fff' : isGameClosed ? '#002129' : ''}}
                        onClick={() => handleButtonClick(game?.GlobalAwayTeamID, topButtonText)}
                        disabled={hasGameStarted || thisIntervalsPick !== undefined || homeSpread === undefined}
                    >
                        {topButtonText}
                    </Button>
                </Box>

                <Box className='super-pickem-game-card-team-row'>
                    <Box className='super-pickem-game-card-logo'>
                        <img
                            src={getTeamLogo(league, game?.GlobalHomeTeamID, teams)}
                            alt={game?.HomeTeam}
                        />
                    </Box>
                    <Typography 
                        className="super-pickem-game-card-team-name"
                        style={{
                            marginLeft: '10px',
                            fontWeight: whoWon === 'home' ? 700 : 500,
                        }}
                    >
                        {getTeamName(league, game?.GlobalHomeTeamID, teams)}
                    </Typography>
                    {isGameClosed && homeScore !== undefined && awayScore !== undefined && (
                        <Typography 
                            className="super-pickem-game-card-score"
                            style={{
                                marginLeft: 'auto',
                                marginRight: '10px',
                                fontWeight: whoWon === 'home' ? 700 : 400,
                                fontSize: isMobile ? '16px' : '18px',
                            }}
                        >
                            {homeScore}
                        </Typography>
                    )}
                    <Button 
                        className='super-pickem-game-card-button'
                        style={{ backgroundColor: getButtonColor(true), color: isBottomSelected ? '#fff' : isGameClosed ? '#002129' : ''}}
                        onClick={() => handleButtonClick(game?.GlobalHomeTeamID, bottomButtonText)}
                        disabled={hasGameStarted || thisIntervalsPick !== undefined || homeSpread === undefined}
                    >
                        {bottomButtonText}
                    </Button>
                </Box>

            </Box>

            <Box className='super-pickem-game-card-bottom-border'>
                <Typography className='super-pickem-game-card-bottom-left-text'>
                    {bottomLeftText}
                </Typography>
                <Typography className='super-pickem-game-card-bottom-right-text'>
                    {bottomRightText}
                </Typography>
            </Box>
        </Box>
    );
}

export default SuperPickemOnePickCard;